import React from 'react';
import { Task, TaskStatus } from '../types/todo';
import { TaskHeader } from './task/TaskHeader';
import { TaskSubtasks } from './task/TaskSubtasks';
import { TaskActions } from './task/TaskActions';
import { TaskNotes } from './task/TaskNotes';
import { TaskResources } from './TaskResources';

interface TaskItemProps {
  task: Task;
  isPreview: boolean;
  departureDate?: string;
  returnDate?: string;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask: (taskId: string) => void;
  onToggleSubtask: (taskId: string, subtaskId: string) => void;
  onUpdateNotes: (taskId: string, notes: string) => void;
  onDeleteSubtask: (taskId: string, subtaskId: string) => void;
  onUpdatePriority: (taskId: string, priority: string) => void;
}

export function TaskItem({
  task,
  isPreview,
  departureDate,
  returnDate,
  onUpdateStatus,
  onEditTask,
  onDeleteTask,
  onAddSubtask,
  onToggleSubtask,
  onUpdateNotes,
  onDeleteSubtask,
  onUpdatePriority
}: TaskItemProps) {
  const taskRef = React.useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const getDaysRemaining = () => {
    if (!departureDate || (task.category === 'return' && !returnDate)) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(task.category === 'return' ? returnDate! : departureDate);
    const diffTime = target.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  const handleTaskClick = (e: React.MouseEvent) => {
    const isInteractiveElement = (e.target as HTMLElement).closest('a, button, input, textarea, [role="button"]');
    if (isInteractiveElement || isPreview) return;
    e.stopPropagation();
  };

  const handleUpdateNotes = () => {
    onUpdateNotes(task.id, task.notes || '');
  };

  const getStatusAccent = () => {
    if (isPreview) return 'sm:border-gray-200 sm:dark:border-gray-700';
    switch (task.status) {
      case 'completed':
        return 'sm:border-l-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10';
      case 'in-progress':
        return 'sm:border-l-amber-400 bg-amber-50/30 dark:bg-amber-900/10';
      default:
        return 'sm:border-l-gray-300 sm:dark:border-l-gray-600';
    }
  };

  return (
    <div
      ref={taskRef}
      id={`task-${task.id}`}
      onClick={handleTaskClick}
      className={`
        group relative
        border-b border-gray-100 dark:border-gray-700/30 last:border-b-0
        sm:rounded-xl sm:border sm:border-gray-100 sm:dark:border-gray-700/50 sm:border-l-[3px]
        transition-all duration-200 bg-white dark:bg-gray-800/50
        ${!isPreview ? 'sm:hover:shadow-md' : ''}
        ${getStatusAccent()}
        ${task.status === 'completed' ? 'opacity-75' : ''}
      `}
    >
      <div className="px-2.5 py-3 sm:p-4">
        <TaskHeader
          isCollapsed={isCollapsed}
          onToggleCollapse={handleToggleCollapse}
          task={task}
          isPreview={isPreview}
          daysRemaining={getDaysRemaining()}
          onUpdateStatus={onUpdateStatus}
          onUpdatePriority={onUpdatePriority}
        />

        <div className={`transition-all duration-200 ${
          isCollapsed ? 'h-0 opacity-0 overflow-hidden' : 'h-auto opacity-100 mt-2 sm:mt-4'
        }`}>
          <div className="space-y-2.5 sm:space-y-4" onClick={e => e.stopPropagation()}>
            {task.subtasks.length > 0 && (
              <TaskSubtasks
                taskId={task.id}
                subtasks={task.subtasks}
                isPreview={isPreview}
                onToggleSubtask={onToggleSubtask}
                onDeleteSubtask={onDeleteSubtask}
              />
            )}

            {!isPreview && (
              <TaskActions
                taskId={task.id}
                isPreview={isPreview}
                onAddSubtask={onAddSubtask}
                onUpdateNotes={handleUpdateNotes}
                hasNotes={!!task.notes}
              />
            )}

            {task.notes && <TaskNotes notes={task.notes} />}

            <TaskResources
              taskId={task.id}
              defaultTaskId={task.defaultTaskId}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
